// ============================================================================
// IncomeSection — the money picture: per-currency total billed, paid vs
// outstanding (as an animated paid-share ring), average fee per session and per
// hour, top-earning clients, and a no-show exclusion callout (per currency).
// ----------------------------------------------------------------------------
// When the trainer has sessions in only ONE currency the layout matches the
// original single-currency view. When multiple currencies are present, one
// labelled block renders per entry, separated by a sub-heading.
//
// This whole section is only mounted by the page when `income.ok` is true (at
// least one delivered session carried a fee) — see BusinessReportPage. So here
// the income is always present; the props type reflects that.
// ============================================================================
import { useTranslation } from "react-i18next";
import { ReportSection } from "./ReportSection";
import { ProgressArc } from "../charts/ProgressArc";
import { RankBars } from "../charts/RankBars";
import type { IncomeSectionOk, CurrencyBlock } from "../types";
import { fmtMoney, clamp } from "../format";

// ----------------------------------------------------------------------------
// Per-currency block
// ----------------------------------------------------------------------------

function IncomeCurrencyBlock({
  blk,
  isMulti,
}: {
  blk: CurrencyBlock;
  isMulti: boolean;
}) {
  const { t, i18n } = useTranslation("businessReport");
  const locale = i18n.language;

  const money = (v: number) =>
    fmtMoney(v, blk.currency, locale, blk.currency_symbol);
  const paidPct =
    blk.total > 0 ? clamp((blk.paid / blk.total) * 100, 0, 100) : 0;

  const topEarners = blk.per_client.slice(0, 6).map((c) => ({
    id: c.client_id,
    label: c.client_name ?? t("common.unnamedClient"),
    value: c.income,
  }));

  // Per-block no-show exclusion (count + fee amount in this currency).
  const noShowBlockCount = blk.no_show_count_excluded ?? 0;

  return (
    <div className="space-y-8">
      {/* Currency sub-heading — only when multiple currencies are present */}
      {isMulti && (
        <h3 className="font-display text-sm font-bold uppercase tracking-kicker text-body-soft">
          {blk.currency}
          {" "}
          <span className="font-normal normal-case text-body-faint">
            ({blk.currency_symbol})
          </span>
        </h3>
      )}

      {/* Total + paid-share ring + paid/outstanding chips */}
      <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-8">
        <ProgressArc
          pct={paidPct}
          accent="lime"
          ariaLabel={`${t("income.paid")} ${Math.round(paidPct)}%`}
        >
          <span className="nums font-display text-3xl font-extrabold leading-none tracking-tightest text-body">
            {Math.round(paidPct)}
            <span className="text-lg">%</span>
          </span>
          <span className="mt-0.5 text-[10px] font-bold uppercase tracking-kicker text-body-faint">
            {t("income.collected")}
          </span>
        </ProgressArc>

        <div className="w-full space-y-4">
          <div className="rounded-lg bg-surface-sunken/70 p-4 text-center ring-1 ring-line-soft">
            <p className="text-xs font-bold uppercase tracking-kicker text-body-soft">
              {t("income.total")}
            </p>
            <p className="nums mt-1 font-display text-3xl font-extrabold tracking-tightest text-body sm:text-4xl">
              {money(blk.total)}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-lime-soft p-4 text-center">
              <p className="nums font-display text-2xl font-extrabold tracking-tightest text-lime-600">
                {money(blk.paid)}
              </p>
              <p className="mt-1 text-xs font-bold uppercase tracking-kicker text-body-soft">
                {t("income.paid")}
              </p>
            </div>
            <div className="rounded-lg bg-amber-soft p-4 text-center">
              <p className="nums font-display text-2xl font-extrabold tracking-tightest text-amber-600">
                {money(blk.outstanding)}
              </p>
              <p className="mt-1 text-xs font-bold uppercase tracking-kicker text-body-soft">
                {t("income.outstanding")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* No-show exclusion callout for this currency block: makes it explicit
          that no-show sessions never added to the total above. */}
      {noShowBlockCount > 0 && (
        <div className="flex items-start gap-3 rounded-lg bg-amber-soft p-4 ring-1 ring-line-soft">
          <span
            aria-hidden
            className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-grad-amber text-white"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none">
              <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="2" />
              <path d="M8 8l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
          <div className="min-w-0">
            <p className="font-display text-sm font-bold text-amber-600">
              {t("income.noShowTitle", { count: noShowBlockCount })}
            </p>
            <p className="mt-0.5 text-xs text-body-soft">
              {t("income.noShowNote")}
              {blk.no_show_excluded > 0 && (
                <>
                  {" · "}
                  <span className="font-bold text-body-muted">
                    {t("income.noShowAmount", {
                      amount: money(blk.no_show_excluded),
                    })}
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Averages */}
      {(blk.avg_per_session !== null || blk.avg_per_hour !== null) && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {blk.avg_per_session !== null && (
            <div className="rounded-lg bg-surface-sunken/60 p-4 ring-1 ring-line-soft">
              <p className="text-xs font-bold uppercase tracking-kicker text-body-soft">
                {t("income.avgPerSession")}
              </p>
              <p className="nums mt-1 font-display text-2xl font-extrabold tracking-tightest text-body">
                {money(blk.avg_per_session)}
              </p>
            </div>
          )}
          {blk.avg_per_hour !== null && (
            <div className="rounded-lg bg-surface-sunken/60 p-4 ring-1 ring-line-soft">
              <p className="text-xs font-bold uppercase tracking-kicker text-body-soft">
                {t("income.avgPerHour")}
              </p>
              <p className="nums mt-1 font-display text-2xl font-extrabold tracking-tightest text-body">
                {money(blk.avg_per_hour)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Top earners */}
      {topEarners.length > 0 && (
        <div>
          <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-kicker text-body-soft">
            {t("income.topEarners")}
          </h3>
          <RankBars data={topEarners} accent="lime" formatValue={money} />
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Section wrapper
// ----------------------------------------------------------------------------

export function IncomeSection({
  income,
  index,
}: {
  income: IncomeSectionOk;
  index: number;
}) {
  const { t } = useTranslation("businessReport");
  const isMulti = income.by_currency.length > 1;

  return (
    <ReportSection
      id="business-income"
      index={index}
      kicker={t("kicker")}
      title={t("income.title")}
      subtitle={t("income.subtitle")}
      hasData
    >
      {/* One block per currency; separated visually by a divider when multi. */}
      <div className={isMulti ? "space-y-12" : "space-y-8"}>
        {income.by_currency.map((blk, i) => (
          <div key={blk.currency}>
            {isMulti && i > 0 && (
              <hr className="mb-12 border-line-soft" />
            )}
            <IncomeCurrencyBlock blk={blk} isMulti={isMulti} />
          </div>
        ))}
      </div>
    </ReportSection>
  );
}
